import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { supabase } from '../../services/supabase';
import {
  MOTION_OPTIONS,
  RATIO_OPTIONS,
  STAGING_ANIMATION_STYLE_OPTIONS,
  type TransitionAnimationSettings,
  DEFAULT_TRANSITION_SETTINGS,
} from '@rehub/transition-animation';

export default function StagingAnimationSettings() {
  const [form, setForm] = useState<TransitionAnimationSettings>(DEFAULT_TRANSITION_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('transition_animation_settings')
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (data) {
          setForm({
            enabled: data.enabled !== false,
            credit_cost: Number(data.credit_cost),
            default_duration: Number(data.default_duration) as TransitionAnimationSettings['default_duration'],
            allowed_durations: data.allowed_durations,
            available_styles: data.available_styles,
            default_style: data.default_style,
            available_aspect_ratios: data.available_aspect_ratios,
            default_motion: data.default_motion,
            provider: data.provider,
            model_endpoint: data.model_endpoint,
            adapter_type: data.adapter_type,
          });
        }
        if (error) setMessage(error.message);
        setLoading(false);
      });
  }, []);

  async function save() {
    setSaving(true);
    setMessage(null);
    const { error } = await supabase
      .from('transition_animation_settings')
      .update({ ...form, updated_at: new Date().toISOString() })
      .eq('id', 1);
    setSaving(false);
    setMessage(error ? error.message : 'Saved');
  }

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;

  return (
    <div className="max-w-2xl space-y-4 text-sm text-slate-200">
      <p className="text-slate-400">
        Commercial settings for Staging Animation. The price shown in Virtual Staging and ImageMotion comes from here.
      </p>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
        />
        Enabled
      </label>
      <Field label="Credit price">
        <input
          type="number"
          min={0}
          value={form.credit_cost}
          onChange={(event) => setForm({ ...form, credit_cost: Number(event.target.value) })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-28"
        />
      </Field>
      <Field label="Default duration">
        <input
          type="number"
          value={form.default_duration}
          onChange={(event) => setForm({ ...form, default_duration: Number(event.target.value) as 5 })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-28"
        />
      </Field>
      <Field label="Allowed durations">
        <input
          value={form.allowed_durations.join(', ')}
          onChange={(event) =>
            setForm({
              ...form,
              allowed_durations: event.target.value.split(',').map((part) => Number(part.trim())).filter((n) => n > 0),
            })
          }
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-full"
        />
      </Field>
      <Field label="Styles">
        <div className="flex flex-wrap gap-2">
          {STAGING_ANIMATION_STYLE_OPTIONS.map((option) => {
            const on = form.available_styles.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  const available_styles = on
                    ? form.available_styles.filter((id) => id !== option.id)
                    : [...form.available_styles, option.id];
                  setForm({ ...form, available_styles });
                }}
                className={`px-2 py-1 rounded border ${on ? 'border-blue-500 text-white' : 'border-slate-700 text-slate-400'}`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Default style">
        <select
          value={form.default_style}
          onChange={(event) => setForm({ ...form, default_style: event.target.value as TransitionAnimationSettings['default_style'] })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1"
        >
          {STAGING_ANIMATION_STYLE_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Aspect ratios">
        <div className="flex flex-wrap gap-2">
          {RATIO_OPTIONS.map((option) => {
            const on = form.available_aspect_ratios.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  const available_aspect_ratios = on
                    ? form.available_aspect_ratios.filter((id) => id !== option.id)
                    : [...form.available_aspect_ratios, option.id];
                  setForm({ ...form, available_aspect_ratios });
                }}
                className={`px-2 py-1 rounded border ${on ? 'border-blue-500 text-white' : 'border-slate-700 text-slate-400'}`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Default motion">
        <select
          value={form.default_motion}
          onChange={(event) => setForm({ ...form, default_motion: event.target.value as TransitionAnimationSettings['default_motion'] })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1"
        >
          {MOTION_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Provider">
        <input
          value={form.provider}
          onChange={(event) => setForm({ ...form, provider: event.target.value })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-full"
        />
      </Field>
      <Field label="Model endpoint">
        <input
          value={form.model_endpoint}
          onChange={(event) => setForm({ ...form, model_endpoint: event.target.value })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-full"
        />
      </Field>
      <Field label="Adapter">
        <input
          value={form.adapter_type}
          onChange={(event) => setForm({ ...form, adapter_type: event.target.value })}
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-full"
        />
      </Field>
      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save
      </button>
      {message && <p className="text-slate-400">{message}</p>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-slate-400">{label}</span>
      {children}
    </label>
  );
}
